import { Controller } from "@hotwired/stimulus"

// Renders editable attribute fields for a product_installer field on the edit_full
// submission form. The container holds hidden inputs that store the form value as
// inspection[data][...][product_type_id], inspection[data][...][attributes][key], and
// optionally inspection[data][...][qtag]. The attribute fields are visible UI elements
// that update the corresponding hidden input via input/change listeners.
export default class extends Controller {
  static targets = ["ptSelect", "ptIdInput", "attributes", "qtagInput", "seedAttr"]
  static values = {
    inputName: String,
    addQtag: Boolean,
    types: Array
  }

  connect() {
    this.seedAttributes = {}
    this.seedAttrTargets.forEach((el) => {
      this.seedAttributes[el.dataset.attrKey] = el.value
      el.remove()
    })
    this.renderAttributes()
  }

  productTypeChanged() {
    this.ptIdInputTarget.value = this.ptSelectTarget.value
    this.seedAttributes = {}
    this.renderAttributes()
  }

  renderAttributes() {
    const selectedId = this.ptSelectTarget.value
    const pt = this.typesValue.find((t) => String(t.id) === String(selectedId))

    this.attributesTarget.innerHTML = ""
    if (!pt) return

    const fields = pt.attribute_fields || []
    fields.forEach((af) => this.attributesTarget.appendChild(this.buildAttributeField(af)))
  }

  buildAttributeField(af) {
    const wrapper = document.createElement("div")
    const baseName = `${this.inputNameValue}[attributes][${af.field_key}]`
    const value = this.seedAttributes[af.field_key] ?? ""
    const required = af.required ? '<span class="text-red-500 ml-1">*</span>' : ""
    const cls = "block w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"

    let inputHtml
    switch (af.field_type) {
      case "number":
        inputHtml = `<input type="number" name="${baseName}" value="${this.escape(value)}" class="${cls}">`
        break
      case "textarea":
        inputHtml = `<textarea name="${baseName}" rows="3" class="${cls}">${this.escape(value)}</textarea>`
        break
      case "select": {
        const opts = (af.options?.options || []).map((o) => {
          const v = this.escape(o.value)
          const l = this.escape(o.label)
          const sel = String(value) === String(o.value) ? "selected" : ""
          return `<option value="${v}" ${sel}>${l}</option>`
        }).join("")
        inputHtml = `<select name="${baseName}" class="${cls}"><option value="">Select…</option>${opts}</select>`
        break
      }
      case "toggle": {
        const checked = value === true || value === "true" || value === "1" || value === "on"
        inputHtml = `
          <input type="hidden" name="${baseName}" value="0">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="${baseName}" value="1" ${checked ? "checked" : ""}
                   class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500">
            <span class="text-xs text-gray-500">Toggle on/off</span>
          </label>`
        break
      }
      case "date":
        inputHtml = `<input type="date" name="${baseName}" value="${this.escape(value)}" class="${cls}">`
        break
      default:
        inputHtml = `<input type="text" name="${baseName}" value="${this.escape(value)}" class="${cls}">`
    }

    wrapper.innerHTML = `
      <label class="block text-xs font-medium text-gray-600 mb-1">${this.escape(af.label)}${required}</label>
      ${inputHtml}
    `
    return wrapper
  }

  qtagChanged(event) {
    if (this.hasQtagInputTarget) {
      this.qtagInputTarget.value = event.target.value
    }
  }

  escape(s) {
    if (s === null || s === undefined) return ""
    return String(s)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
